/**
 * Page Builder Studio - Kimi-Style Canvas-First Editor
 * 
 * EXACT UI PARITY WITH KIMI PAGE BUILDER:
 * - Left: ADD SECTION sidebar (simple list)
 * - Center: Canvas with grid, selection glow, floating toolbar
 * - Right: EDIT panel with accordion sections
 * - Top: Edit mode, logo, page selector, undo/redo, share, publish
 * 
 * Mental Model: You are editing the PAGE itself, not managing blocks.
 * Selection-based interaction with glow effect and floating controls.
 */
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Monitor, Tablet, Smartphone, Plus, Crosshair } from 'lucide-react';
import { Button } from './ui/button';

// Kimi-style components
import {
  KimiEditPanel,
  KimiTopBar,
  KimiCanvas,
  KimiSectionWrapper,
  PagePreviewRenderer,
  LandingPagePreviewRenderer,
  ElementInspector,
  DesignTokensEditor,
  SelectionOverlay,
  useDesignTokens,
  injectTokensAsCSSVars,
  getElementById,
  setElementValue,
  setElementStyles,
  setElementMotion,
} from './page-builder';

// WYSIWYG imports
import { EditorStoreProvider } from './page-builder/wysiwyg/useEditorStore';
import WysiwygOverlay from './page-builder/wysiwyg/WysiwygOverlay';
import useCanvasAnnotator from './page-builder/wysiwyg/CanvasAnnotator';
import NewPageModal from './page-builder/wysiwyg/NewPageModal';
import { UndoRedoController, useHistoryCommit } from './page-builder/wysiwyg/UndoRedoController';

// Builder Composition imports
import AddSectionPanel, { DATA_BLOCK_TYPES } from './page-builder/AddSectionPanel';
import DataRowInspector from './page-builder/DataRowInspector';
import ComposableBlockInspector, { isComposableBlock } from './page-builder/ComposableBlockInspector';

import { API_URL } from '../config/api';

// Import Immutable Style Foundation
import {
  TOKEN_SCOPE_CLASS,
  injectTokenCSS,
  removeTokenCSS,
  applyTokenScope,
  createResolver,
  StyleSourceLabel,
  defaultTokens,
  DEFAULT_LAYOUT,
  DEFAULT_BACKGROUND,
  TemplatePicker,
  AddElementsPanel,
  createBlockFromTemplate,
  coercePageConfig,
  validatePageConfig,
} from '../admin/page_builder';

const API = API_URL;

// Pages with direct rendering
const DIRECT_RENDER_PAGES = ['homepage', 'landing_early_access'];

const PageBuilderStudio = ({ pageIdOverride }) => {
  const { pageId: paramPageId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previewRef = useRef(null);
  const canvasRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const savedScrollTopRef = useRef(null);
  
  // New Page modal state
  const [showNewPageModal, setShowNewPageModal] = useState(false);
  
  // Design tokens
  const designTokensContext = useDesignTokens();
  const tokens = useMemo(() => designTokensContext?.tokens || {}, [designTokensContext?.tokens]);
  
  // Page ID resolution
  const pageId = useMemo(() => {
    if (pageIdOverride) return pageIdOverride;
    if (paramPageId) return paramPageId;
    if (location.pathname.includes('/homepage')) return 'homepage';
    if (location.pathname.includes('/landing')) return 'landing_early_access';
    return 'landing_early_access';
  }, [pageIdOverride, paramPageId, location.pathname]);
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [blockRegistry, setBlockRegistry] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  
  // UI state
  const [viewport, setViewport] = useState('desktop');
  const [showDesignTokens, setShowDesignTokens] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  
  // Selection state - Kimi style (section-first, element-second)
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [hoveredSectionId, setHoveredSectionId] = useState(null);

  // Style resolver
  const styleResolver = useMemo(() => {
    return createResolver(tokens || defaultTokens);
  }, [tokens]);

  // Token injection
  useEffect(() => {
    const mergedTokens = { ...defaultTokens, ...tokens };
    injectTokenCSS(mergedTokens);
    return () => removeTokenCSS();
  }, [tokens]);

  useEffect(() => {
    if (previewRef.current) {
      applyTokenScope(previewRef.current);
    }
  }, [previewKey]);

  useEffect(() => {
    if (previewRef.current && tokens) {
      injectTokensAsCSSVars(tokens, previewRef.current);
    }
  }, [tokens, previewKey]);

  // Direct render check
  const useDirectRender = DIRECT_RENDER_PAGES.includes(pageId);

  // Selected section data
  const selectedSection = useMemo(() => {
    if (!selectedSectionId || !pageData) return null;
    return pageData.blocks.find(b => b.id === selectedSectionId);
  }, [selectedSectionId, pageData]);

  // Get section index for display
  const selectedSectionIndex = useMemo(() => {
    if (!selectedSectionId || !pageData) return 0;
    const sorted = [...pageData.blocks].sort((a, b) => a.order - b.order);
    return sorted.findIndex(b => b.id === selectedSectionId);
  }, [selectedSectionId, pageData]);

  // Selected element schema
  const selectedElement = useMemo(() => {
    if (!selectedElementId || !selectedSectionId) return null;
    const block = pageData?.blocks?.find(b => b.id === selectedSectionId);
    if (!block) return null;
    return getElementById(block.type, selectedElementId);
  }, [selectedElementId, selectedSectionId, pageData]);

  // Resolved styles for selected element
  const resolvedElementStyles = useMemo(() => {
    if (!selectedElement || !selectedSectionId || !pageData) return null;
    
    const block = pageData.blocks.find(b => b.id === selectedSectionId);
    if (!block) return null;

    const componentDefaults = selectedElement.defaultStyles || {};
    const pageOverrides = pageData.pageStyleOverrides || {};
    const blockOverrides = block.styleOverrides || {};
    const elementOverrides = block.elementOverrides?.[selectedElementId] || {};

    return styleResolver.resolve({
      componentDefaults,
      pageOverrides,
      blockOverrides,
      elementOverrides
    });
  }, [selectedElement, selectedSectionId, selectedElementId, pageData, styleResolver]);

  // Fetch data
  useEffect(() => {
    if (pageId) fetchData();
  }, [pageId]);

  // Track changes
  useEffect(() => {
    if (pageData && originalData) {
      const changed = JSON.stringify(pageData.blocks) !== JSON.stringify(originalData.blocks);
      setHasChanges(changed);
    }
  }, [pageData, originalData]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [pageRes, registryRes] = await Promise.all([
        axios.get(`${API}/admin/page-builder/pages/${pageId}?version=draft`, { headers }),
        axios.get(`${API}/admin/page-builder/blocks`, { headers })
      ]);
      
      // Coerce blocks to valid defaults if constraints violated
      const rawBlocks = pageRes.data.blocks || [];
      const coercedBlocks = coercePageConfig(rawBlocks);
      
      // Check if any coercion happened and notify user
      const wasCoerced = coercedBlocks.some(b => b._wasCoerced);
      if (wasCoerced) {
        toast.info('Some section settings were adjusted to match template constraints', {
          duration: 4000,
        });
        // Clean up coercion flag
        coercedBlocks.forEach(b => delete b._wasCoerced);
      }
      
      setPageData({ ...pageRes.data, blocks: coercedBlocks });
      setOriginalData(JSON.parse(JSON.stringify({ ...pageRes.data, blocks: coercedBlocks })));
      setBlockRegistry(registryRes.data.blocks);
    } catch (error) {
      console.error('Error fetching page data:', error);
      toast.error('Failed to load page data');
      navigate('/admin/page-builder');
    } finally {
      setLoading(false);
    }
  };

  // Section selection - Kimi style (clicking section selects it)
  // Save scrollTop BEFORE state change; useLayoutEffect will restore it
  const handleSelectSection = useCallback((sectionId) => {
    savedScrollTopRef.current = scrollContainerRef.current?.scrollTop ?? null;
    setSelectedSectionId(sectionId);
    setSelectedElementId(null);
  }, []);

  // Canvas click deselects
  const handleCanvasClick = useCallback(() => {
    savedScrollTopRef.current = scrollContainerRef.current?.scrollTop ?? null;
    setSelectedSectionId(null);
    setSelectedElementId(null);
  }, []);

  // Restore scroll position after React re-renders due to selection change.
  // useLayoutEffect fires synchronously after DOM mutations but before paint,
  // so the user never sees a scroll jump.
  useLayoutEffect(() => {
    if (savedScrollTopRef.current !== null && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedScrollTopRef.current;
      savedScrollTopRef.current = null;
    }
  }, [selectedSectionId, selectedElementId]);

  // SCROLL FIX: Save scroll position on mousedown (before click triggers scroll jumps)
  // mousedown fires BEFORE the browser scrolls elements into view
  // We use previewKey as dependency to re-run after initial render when ref is populated
  useEffect(() => {
    // Check for ref availability using a small delay to ensure it's populated
    const timer = setTimeout(() => {
      if (!scrollContainerRef.current) return;
      
      const scrollContainer = scrollContainerRef.current;
      
      const handleMouseDown = () => {
        // Save current scroll position immediately on mousedown
        if (scrollContainerRef.current) {
          savedScrollTopRef.current = scrollContainerRef.current.scrollTop;
        }
      };
      
      scrollContainer.addEventListener('mousedown', handleMouseDown, true);
      
      // Store cleanup reference
      scrollContainer._mousedownHandler = handleMouseDown;
    }, 100);
    
    return () => {
      clearTimeout(timer);
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer && scrollContainer._mousedownHandler) {
        scrollContainer.removeEventListener('mousedown', scrollContainer._mousedownHandler, true);
      }
    };
  }, [previewKey]); // Re-run when previewKey changes (after data loads)

  // "Center Selection" — scroll the selected block into view (user-initiated only)
  const handleCenterSelection = useCallback(() => {
    if (!selectedSectionId || !scrollContainerRef.current) return;
    const blockEl = scrollContainerRef.current.querySelector(
      `[data-block-id="${selectedSectionId}"]`
    );
    if (blockEl) {
      blockEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedSectionId]);


  // Add section from template
  const handleAddSection = useCallback((sectionType) => {
    // Map section type to template
    const templateMap = {
      hero: 'hero_overlay',
      content: 'trending_rail',
      media_grid: 'featured_grid',
      comic_panel: 'chapter_list',
      chapter: 'chapter_list',
      gallery: 'art_gallery_grid',
      profile_header: 'creator_spotlight',
      stats: 'leaderboard_strip',
      comments: 'transparency_footer',
      custom: null,
    };
    
    const templateId = templateMap[sectionType];
    
    if (sectionType === 'custom' || !templateId) {
      setShowTemplatePicker(true);
      return;
    }
    
    const newBlock = createBlockFromTemplate(templateId);
    if (!newBlock) {
      setShowTemplatePicker(true);
      return;
    }

    setPageData(prev => {
      const maxOrder = Math.max(...prev.blocks.map(b => b.order), -1);
      newBlock.order = maxOrder + 1;
      const newState = { ...prev, blocks: [...prev.blocks, newBlock] };
      
      // Commit to history
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      
      return newState;
    });
    
    setSelectedSectionId(newBlock.id);
    setHasChanges(true);
    toast.success(`Added section`);
  }, []);

  const handleAddFromTemplate = useCallback((template) => {
    const newBlock = createBlockFromTemplate(template.id);
    if (!newBlock) return;

    setPageData(prev => {
      const maxOrder = Math.max(...prev.blocks.map(b => b.order), -1);
      newBlock.order = maxOrder + 1;
      const newState = { ...prev, blocks: [...prev.blocks, newBlock] };
      
      // Commit to history
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      
      return newState;
    });
    
    setSelectedSectionId(newBlock.id);
    setHasChanges(true);
    setShowTemplatePicker(false);
    toast.success(`Added "${template.name}" section`);
  }, []);

  // =========================================================================
  // Builder Composition: Add Block (container-aware insert)
  // =========================================================================
  const handleAddBlock = useCallback((newBlock) => {
    setPageData(prev => {
      const blocks = [...(prev.blocks || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

      // Determine insert position
      let insertIndex = blocks.length; // Default: append
      if (selectedSectionId) {
        const selectedIdx = blocks.findIndex(b => b.id === selectedSectionId);
        if (selectedIdx >= 0) {
          insertIndex = selectedIdx + 1; // Insert after selected
        }
      }

      // Insert and recalculate orders
      blocks.splice(insertIndex, 0, newBlock);
      blocks.forEach((b, i) => { b.order = i; });

      const newState = { ...prev, blocks };
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      return newState;
    });

    savedScrollTopRef.current = scrollContainerRef.current?.scrollTop ?? null;
    setSelectedSectionId(newBlock.id);
    setSelectedElementId(null);
    setHasChanges(true);

    const label = newBlock.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    toast.success(`Added ${label}`);
  }, [selectedSectionId]);

  // =========================================================================
  // Section Reordering: Move Up / Move Down
  // =========================================================================
  const handleMoveUp = useCallback((blockId) => {
    setPageData(prev => {
      const blocks = [...prev.blocks].sort((a, b) => (a.order || 0) - (b.order || 0));
      const idx = blocks.findIndex(b => b.id === blockId);
      if (idx <= 0) return prev;

      [blocks[idx - 1], blocks[idx]] = [blocks[idx], blocks[idx - 1]];
      blocks.forEach((b, i) => { b.order = i; });

      const newState = { ...prev, blocks };
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      return newState;
    });
    setHasChanges(true);
  }, []);

  const handleMoveDown = useCallback((blockId) => {
    setPageData(prev => {
      const blocks = [...prev.blocks].sort((a, b) => (a.order || 0) - (b.order || 0));
      const idx = blocks.findIndex(b => b.id === blockId);
      if (idx < 0 || idx >= blocks.length - 1) return prev;

      [blocks[idx], blocks[idx + 1]] = [blocks[idx + 1], blocks[idx]];
      blocks.forEach((b, i) => { b.order = i; });

      const newState = { ...prev, blocks };
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      return newState;
    });
    setHasChanges(true);
  }, []);

  // Helper: is this a data row block?
  const isDataRowBlock = useCallback((type) => type in DATA_BLOCK_TYPES, []);

  // Section operations
  const handleDuplicateSection = useCallback((sectionId) => {
    setPageData(prev => {
      const section = prev.blocks.find(b => b.id === sectionId);
      if (!section) return prev;
      
      const newSection = {
        ...JSON.parse(JSON.stringify(section)),
        id: `${section.type}_${Date.now()}`,
        order: Math.max(...prev.blocks.map(b => b.order)) + 1,
      };
      
      const newState = { ...prev, blocks: [...prev.blocks, newSection] };
      
      // Commit to history
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      
      return newState;
    });
    setHasChanges(true);
    toast.success('Section duplicated');
  }, []);

  const handleDeleteSection = useCallback((sectionId) => {
    savedScrollTopRef.current = scrollContainerRef.current?.scrollTop ?? null;
    setPageData(prev => {
      const newState = {
        ...prev,
        blocks: prev.blocks.filter(b => b.id !== sectionId)
      };
      
      // Commit to history
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      
      return newState;
    });
    if (selectedSectionId === sectionId) {
      setSelectedSectionId(null);
      setSelectedElementId(null);
    }
    setHasChanges(true);
    toast.success('Section deleted');
  }, [selectedSectionId]);

  // Block props updates
  const handleUpdateProps = useCallback((key, value) => {
    if (!selectedSectionId) return;
    setPageData(prev => {
      const newState = {
        ...prev,
        blocks: prev.blocks.map(b => {
          if (b.id !== selectedSectionId) return b;
          // Special key: toggle enabled state
          if (key === '__enabled') return { ...b, enabled: value };
          return { ...b, props: { ...b.props, [key]: value } };
        })
      };
      
      // Commit to history for significant changes
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      
      return newState;
    });
    setHasChanges(true);
  }, [selectedSectionId]);

  const handleUpdateLayout = useCallback((newLayout) => {
    if (!selectedSectionId) return;
    setPageData(prev => {
      const newState = {
        ...prev,
        blocks: prev.blocks.map(b =>
          b.id === selectedSectionId ? { ...b, layout: newLayout } : b
        )
      };
      
      // Commit to history
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      
      return newState;
    });
    setHasChanges(true);
  }, [selectedSectionId]);

  const handleUpdateBackground = useCallback((newBackground) => {
    if (!selectedSectionId) return;
    setPageData(prev => {
      const newState = {
        ...prev,
        blocks: prev.blocks.map(b =>
          b.id === selectedSectionId ? { ...b, background: newBackground } : b
        )
      };
      
      // Commit to history
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      
      return newState;
    });
    setHasChanges(true);
  }, [selectedSectionId]);

  // Element operations
  const handleSelectElement = useCallback((elementId) => {
    savedScrollTopRef.current = scrollContainerRef.current?.scrollTop ?? null;
    setSelectedElementId(elementId);
  }, []);

  const handleUpdateElementValue = useCallback((blockId, elementId, newValue) => {
    const block = pageData?.blocks?.find(b => b.id === blockId);
    if (!block) return;
    
    const element = getElementById(block.type, elementId);
    if (!element) return;
    
    const newProps = setElementValue(block.props || {}, element, newValue);
    
    setPageData(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => 
        b.id === blockId ? { ...b, props: newProps } : b
      )
    }));
  }, [pageData]);

  const handleUpdateElementStyles = useCallback((blockId, elementId, newStyles) => {
    const block = pageData?.blocks?.find(b => b.id === blockId);
    if (!block) return;
    
    const element = getElementById(block.type, elementId);
    if (!element) return;
    
    const newProps = setElementStyles(block.props || {}, element, newStyles);
    
    setPageData(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => 
        b.id === blockId ? { ...b, props: newProps } : b
      )
    }));
  }, [pageData]);

  const handleUpdateElementMotion = useCallback((blockId, elementId, newMotion) => {
    const block = pageData?.blocks?.find(b => b.id === blockId);
    if (!block) return;
    
    const element = getElementById(block.type, elementId);
    if (!element) return;
    
    const newProps = setElementMotion(block.props || {}, element, newMotion);
    
    setPageData(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => 
        b.id === blockId ? { ...b, props: newProps } : b
      )
    }));
  }, [pageData]);

  // Save/Publish operations
  const handleSaveDraft = async () => {
    // Validate before saving
    const validation = validatePageConfig(pageData?.blocks || []);
    if (!validation.valid) {
      // Log validation errors but don't block save (constraints already coerced on load)
      console.warn('[PageBuilder] Validation warnings:', validation);
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/page-builder/pages/${pageId}/draft`,
        { blocks: pageData.blocks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOriginalData(JSON.parse(JSON.stringify(pageData)));
      setHasChanges(false);
      setPreviewKey(prev => prev + 1);
      toast.success('Saved');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    // Validate strictly before publishing
    const validation = validatePageConfig(pageData?.blocks || []);
    if (!validation.valid) {
      const errorCount = validation.sectionErrors.length + validation.blockCountErrors.length;
      toast.error(`Cannot publish: ${errorCount} validation error(s)`, {
        description: validation.sectionErrors[0]?.errors[0] || validation.blockCountErrors[0],
        duration: 5000,
      });
      return;
    }
    
    setPublishing(true);
    try {
      const token = localStorage.getItem('token');
      
      if (hasChanges) {
        await axios.put(
          `${API}/admin/page-builder/pages/${pageId}/draft`,
          { blocks: pageData.blocks },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      await axios.post(
        `${API}/admin/page-builder/pages/${pageId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setOriginalData(JSON.parse(JSON.stringify(pageData)));
      setHasChanges(false);
      toast.success('Published!');
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  // Undo/Redo - Now connected to real history
  const handleUndo = useCallback(() => {
    // Will be connected to EditorStore via UndoRedoController component
    window.dispatchEvent(new CustomEvent('editor:undo'));
  }, []);
  
  const handleRedo = useCallback(() => {
    // Will be connected to EditorStore via UndoRedoController component
    window.dispatchEvent(new CustomEvent('editor:redo'));
  }, []);
  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied!');
  };

  // Auto-save on changes
  useEffect(() => {
    if (hasChanges) {
      const timer = setTimeout(handleSaveDraft, 2000);
      return () => clearTimeout(timer);
    }
  }, [hasChanges, pageData]);

  // WYSIWYG Canvas Annotator
  useCanvasAnnotator(canvasRef, pageData?.blocks);

  // WYSIWYG commit handler: called by WysiwygOverlay when user finishes drag/resize/text
  const handleWysiwygCommit = useCallback((changeType, elementId, blockId, data) => {
    if (!pageData || !elementId) return;
    
    // Parse elementId: "blockId::propKey"
    const [, propKey] = elementId.split('::');
    if (!propKey) return;
    
    // Find the actual blockId from the element's block
    const actualBlockId = blockId || elementId.split('::')[0];
    
    setPageData(prev => {
      if (!prev) return prev;
      const blocks = prev.blocks.map(block => {
        if (block.id !== actualBlockId) return block;
        
        const updatedProps = { ...block.props };
        
        switch (changeType) {
          case 'text':
            updatedProps[propKey] = data.text;
            break;
          case 'drag':
            // Store position offset in a layout sub-object
            updatedProps._layout = {
              ...(updatedProps._layout || {}),
              [propKey]: {
                ...(updatedProps._layout?.[propKey] || {}),
                offsetX: (updatedProps._layout?.[propKey]?.offsetX || 0) + (data.dx || 0),
                offsetY: (updatedProps._layout?.[propKey]?.offsetY || 0) + (data.dy || 0),
              },
            };
            break;
          case 'resize':
            updatedProps._layout = {
              ...(updatedProps._layout || {}),
              [propKey]: {
                ...(updatedProps._layout?.[propKey] || {}),
                width: data.w,
                height: data.h,
              },
            };
            break;
          default:
            break;
        }
        
        return { ...block, props: updatedProps };
      });
      
      const newState = { ...prev, blocks };
      
      // Dispatch history commit event for UndoRedoController
      window.dispatchEvent(new CustomEvent('editor:commit', { detail: { pageState: newState } }));
      
      return newState;
    });
    
    setHasChanges(true);
    
    // Debounced save after WYSIWYG changes (500ms)
    clearTimeout(window._wysiwygSaveTimer);
    window._wysiwygSaveTimer = setTimeout(() => {
      handleSaveDraft();
    }, 500);
  }, [pageData, handleSaveDraft]);

  // Get selected block props for element inspector
  const selectedBlockProps = useMemo(() => {
    if (!selectedSectionId || !pageData) return {};
    const block = pageData.blocks.find(b => b.id === selectedSectionId);
    return block?.props || {};
  }, [selectedSectionId, pageData]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="page-builder-loading">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400">Page not found</p>
          <Button onClick={() => navigate('/admin/page-builder')} className="mt-4">
            Back to Page Builder
          </Button>
        </div>
      </div>
    );
  }

  const schema = selectedSection ? blockRegistry[selectedSection.type] : null;

  return (
    <EditorStoreProvider>
      {/* Undo/Redo Controller - handles keyboard shortcuts and history */}
      <UndoRedoController
        pageData={pageData}
        setPageData={setPageData}
        setHasChanges={setHasChanges}
        onSave={handleSaveDraft}
      />
    <div className="min-h-screen bg-zinc-950 flex flex-col" data-testid="page-builder-studio">
      {/* Kimi-style Top Bar */}
      <KimiTopBar
        pageName={pageData.name}
        pageRoute={pageData.route}
        hasChanges={hasChanges}
        saving={saving}
        publishing={publishing}
        onSave={handleSaveDraft}
        onPublish={handlePublish}
        onUndo={handleUndo}
        onRedo={handleRedo}
        onShare={handleShare}
        onPreview={() => {
          // Open the public page route in a new tab (same fetch path as visitors)
          const route = pageData.route || '/';
          window.open(route, '_blank');
        }}
        extraActions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowNewPageModal(true)}
            className="border-zinc-700 text-zinc-300 hover:text-white hover:bg-zinc-800 h-8 text-[12px] gap-1"
            data-testid="new-page-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            New Page
          </Button>
        }
      />

      {/* New Page Modal */}
      <NewPageModal
        open={showNewPageModal}
        onClose={() => setShowNewPageModal(false)}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: ADD SECTION panel */}
        <AddSectionPanel
          onAddBlock={handleAddBlock}
          currentBlocks={pageData.blocks}
        />

        {/* Add Elements Panel (Wix-style 3-pane) */}
        <AddElementsPanel
          isOpen={showTemplatePicker}
          onClose={() => setShowTemplatePicker(false)}
          onSelectTemplate={handleAddFromTemplate}
          pageType={pageId}
        />

        {/* Center: Canvas */}
        <KimiCanvas
          ref={scrollContainerRef}
          onCanvasClick={handleCanvasClick}
          viewport={viewport}
        >
          <div 
            ref={(el) => { previewRef.current = el; canvasRef.current = el; }}
            className={TOKEN_SCOPE_CLASS}
          >
            {pageId === 'homepage' ? (
              <PagePreviewRenderer
                key={previewKey}
                pageId={pageId}
                blocks={pageData.blocks}
                selectedBlockId={selectedSectionId}
                onSelectBlock={handleSelectSection}
                viewport={viewport}
                onSectionHover={(id, hovered) => setHoveredSectionId(hovered ? id : null)}
                onDuplicateSection={handleDuplicateSection}
                onDeleteSection={handleDeleteSection}
              />
            ) : (
              // Use LandingPagePreviewRenderer for all other pages (including custom pages)
              // This handles landing_* block types properly
              <LandingPagePreviewRenderer
                key={previewKey}
                blocks={pageData.blocks}
                selectedBlockId={selectedSectionId}
                onSelectBlock={handleSelectSection}
                selectedElementId={selectedElementId}
                onSelectElement={handleSelectElement}
                editingElementId={null}
                onStartEditing={() => {}}
                onStopEditing={() => {}}
                onUpdateElementValue={handleUpdateElementValue}
                viewport={viewport}
                animationKey={previewKey}
                styleResolver={styleResolver}
                onDuplicateBlock={handleDuplicateSection}
                onDeleteBlock={handleDeleteSection}
                onMoveBlockUp={handleMoveUp}
                onMoveBlockDown={handleMoveDown}
              />
            )}
          </div>
          
          {/* WYSIWYG Overlay for selection/drag/resize/inline-edit */}
          <WysiwygOverlay
            canvasRef={canvasRef}
            onCommitChanges={handleWysiwygCommit}
            onSelectElement={(elementId, blockId) => {
              savedScrollTopRef.current = scrollContainerRef.current?.scrollTop ?? null;
              if (blockId) setSelectedSectionId(blockId);
              setSelectedElementId(elementId);
            }}
            selectedElementId={selectedElementId}
            selectedBlockId={selectedSectionId}
          />
        </KimiCanvas>
        
        {/* Viewport toggles - floating bottom of canvas */}
        <div 
          className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-zinc-900/90 backdrop-blur-sm rounded-lg p-1 border border-zinc-700/50 z-50"
        >
          {[
            { key: 'desktop', icon: Monitor, label: 'Desktop' },
            { key: 'tablet', icon: Tablet, label: 'Tablet' },
            { key: 'mobile', icon: Smartphone, label: 'Mobile' },
          ].map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => setViewport(key)}
              className={`p-2 rounded transition-colors ${
                viewport === key
                  ? 'bg-zinc-700 text-white'
                  : 'text-zinc-500 hover:text-white hover:bg-zinc-800'
              }`}
              title={label}
              data-testid={`viewport-${key}`}
            >
              <Icon className="w-4 h-4" />
            </button>
          ))}
          {/* Center Selection — only visible when a section is selected */}
          {selectedSectionId && (
            <>
              <div className="w-px h-4 bg-zinc-700/50 mx-0.5" />
              <button
                onClick={handleCenterSelection}
                className="p-2 rounded text-zinc-500 hover:text-white hover:bg-zinc-800 transition-colors"
                title="Center Selection"
                data-testid="center-selection-btn"
              >
                <Crosshair className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
        
        {/* Selection Overlay (Element-level, floating toolbar) */}
        {selectedElementId && selectedSectionId && (
          <SelectionOverlay
            selectedElementId={selectedElementId}
            selectedBlockId={selectedSectionId}
            blockProps={selectedBlockProps}
            tokens={tokens}
            containerRef={previewRef}
            onUpdateValue={(newValue) => handleUpdateElementValue(selectedSectionId, selectedElementId, newValue)}
            onUpdateStyles={(newStyles) => handleUpdateElementStyles(selectedSectionId, selectedElementId, newStyles)}
            onUpdateMotion={(newMotion) => handleUpdateElementMotion(selectedSectionId, selectedElementId, newMotion)}
            onReplayAnimation={() => setPreviewKey(prev => prev + 1)}
            onDeselectElement={() => setSelectedElementId(null)}
          />
        )}

        {/* Right: EDIT panel */}
        {selectedElement && selectedSectionId ? (
          <aside className="w-[260px] bg-zinc-950 border-l border-zinc-800/30 flex flex-col" data-testid="element-inspector">
            <ElementInspector
              element={selectedElement}
              blockProps={selectedBlockProps}
              onUpdateValue={(newValue) => handleUpdateElementValue(selectedSectionId, selectedElementId, newValue)}
              onUpdateStyles={(newStyles) => handleUpdateElementStyles(selectedSectionId, selectedElementId, newStyles)}
              onUpdateMotion={(newMotion) => handleUpdateElementMotion(selectedSectionId, selectedElementId, newMotion)}
              onReplayAnimation={() => setPreviewKey(prev => prev + 1)}
              onClose={() => setSelectedElementId(null)}
              resolvedStyles={resolvedElementStyles}
              styleSourceLabels={StyleSourceLabel}
            />
          </aside>
        ) : selectedSection && isDataRowBlock(selectedSection.type) ? (
          <DataRowInspector
            section={selectedSection}
            onUpdateProps={handleUpdateProps}
          />
        ) : selectedSection && isComposableBlock(selectedSection.type) ? (
          <ComposableBlockInspector
            section={selectedSection}
            onUpdateProps={handleUpdateProps}
          />
        ) : (
          <KimiEditPanel
            section={selectedSection}
            sectionIndex={selectedSectionIndex}
            schema={schema}
            onUpdateProps={handleUpdateProps}
            onUpdateLayout={handleUpdateLayout}
            onUpdateBackground={handleUpdateBackground}
          />
        )}
      </div>
      
      {/* Design Tokens Editor Modal */}
      <DesignTokensEditor
        open={showDesignTokens}
        onClose={() => setShowDesignTokens(false)}
      />
    </div>
    </EditorStoreProvider>
  );
};

export default PageBuilderStudio;
